import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { IoArrowBack, IoChevronForward, IoSchoolOutline } from 'react-icons/io5';
import { styled } from '@mui/material/styles';
import Select, { SingleValue } from 'react-select';
import { Icon } from '@iconify/react';

import moment from 'moment';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';

import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import { UserListHead } from 'src/sections/@dashboard/user';
import { ToastNotifications, TransactionModal } from 'src/components';
import LineChart from 'src/components/LineChart';
import DonutChart from 'src/components/DonutChart';
import { PaycheckStub } from 'src/pages/Admin/payroll/components/PaycheckStub';

import { studentService } from 'src/services/student.service';
import { transactionService } from 'src/services/transaction.service';
import { loanService } from 'src/services/loan.service';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { studentBehaviourService } from 'src/services/student-behaviour.service';
import { badgeService } from 'src/services/badge.service';
import { savingsAccountService } from 'src/services/savingsAccount.service';
import { payrollImportService, formatMoney } from 'src/services/payroll';
import { cohortService } from 'src/services/cohort.service';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';
import type { PayrollSnapshot } from 'src/types/payroll.types';
import palette from 'src/theme/palette';
import 'react-toastify/dist/ReactToastify.css';

interface BadgeOption {
  label: React.ReactNode;
  value: React.ReactNode;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2.5),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
  },
}));

type TabKey =
  | 'transactions'
  | 'pending'
  | 'paychecks'
  | 'savings'
  | 'loans'
  | 'street-cred'
  | 'simulations'
  | 'cohorts';

export default function ViewStudent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { studentId = '' } = useParams<{ studentId: string }>();
  const queryClient = useQueryClient();

  const isAdmin = location.pathname.startsWith('/admin');
  const routePrefix = isAdmin ? '/admin' : '/teacher';

  // Active Tab & Filter State
  const [activeTab, setActiveTab] = useState<TabKey>('transactions');
  const [txTypeFilter, setTxTypeFilter] = useState<'ALL' | 'CREDIT' | 'DEBIT'>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modals State
  const [isTransactionModalOpen, setTransactionModalOpen] = useState<boolean>(false);
  const [isBadgeModalOpen, setBadgeModalOpen] = useState<boolean>(false);
  const [isAnalyticsModalOpen, setAnalyticsModalOpen] = useState<boolean>(false);
  const [selectedBadge, setSelectedBadge] = useState<SingleValue<BadgeOption>>(null);
  const [badgeLoading, setBadgeLoading] = useState<boolean>(false);
  const [approvingTxId, setApprovingTxId] = useState<string | null>(null);
  const [cancellingTxId, setCancellingTxId] = useState<string | null>(null);

  // Paycheck Stub modal state
  const [selectedPaycheck, setSelectedPaycheck] = useState<{
    snapshot: PayrollSnapshot;
    amount: number;
    createdAt: string;
  } | null>(null);

  // 1. Fetch Student Profile
  const { data: student, isLoading: isLoadingStudent } = useQuery({
    queryKey: ['student', studentId],
    queryFn: () => studentService.getStudentById(studentId),
    enabled: !!studentId,
  });

  // 2. Fetch Badges
  const { data: badgeData } = useQuery({
    queryKey: ['badges'],
    queryFn: () => badgeService.getBadges({ page: 1, limit: 1000 }),
  });

  // 3. Fetch Financial Summary
  const { data: financialSummary } = useQuery({
    queryKey: ['student-financial-summary', studentId],
    queryFn: () => studentService.getStudentFinancialSummary(studentId),
    enabled: !!studentId,
  });

  // 4. Fetch Monthly Balance History
  const { data: monthlyBalance } = useQuery({
    queryKey: ['student-monthly-balance', studentId],
    queryFn: () => studentService.getMonthlyBalance(studentId),
    enabled: !!studentId,
  });

  // 5. Fetch Behavior Count
  const { data: behaviorCount } = useQuery({
    queryKey: ['student-behavior-count', studentId],
    queryFn: () => studentService.getStudentBehaviorCount(studentId),
    enabled: !!studentId,
  });

  // 6. Fetch S.T.R.E.E.T. Cred Behavior List
  const { data: streetCredData } = useQuery({
    queryKey: ['street-cred', studentId],
    queryFn: () => studentBehaviourService.getStudentBehaviours({ studentId }),
    enabled: !!studentId,
  });

  // 7. Fetch Transactions
  const { data: transactionsData, isLoading: isLoadingTransactions } = useQuery({
    queryKey: ['transactions', studentId],
    queryFn: () => transactionService.getTransactions(1, 1000, { studentId }),
    enabled: !!studentId,
  });

  // 8. Fetch Loans & Loan Summary
  const { data: loansData, isLoading: isLoadingLoans } = useQuery({
    queryKey: ['loans', studentId],
    queryFn: () => loanService.getLoans({ studentId }),
    enabled: !!studentId,
  });

  const { data: loanSummary } = useQuery({
    queryKey: ['loan-summary', studentId],
    queryFn: () => loanService.getLoanSummary(studentId),
    enabled: !!studentId,
  });

  // 9. Fetch Savings Accounts
  const { data: savingsAccounts = [], isLoading: isLoadingSavings } = useQuery({
    queryKey: ['savings-accounts', studentId],
    queryFn: () => savingsAccountService.getStudentSavingsAccounts(studentId),
    enabled: !!studentId,
  });

  // 10. Fetch Paychecks
  const { data: paychecks = [], isLoading: isLoadingPaychecks } = useQuery({
    queryKey: ['student-paychecks', studentId],
    queryFn: () => payrollImportService.listStudentPaychecks(studentId),
    enabled: !!studentId,
  });

  // 11. Fetch Financial Simulations
  const { data: financialSimulations, isLoading: isLoadingSimulations } = useQuery({
    queryKey: ['financial-simulations', studentId],
    queryFn: () => financialSimulationService.getChallenges({ page: 1, limit: 1000 }),
    enabled: !!studentId,
  });

  // 12. Fetch Cohorts
  const { data: allCohorts = [] } = useQuery({
    queryKey: ['cohorts-list'],
    queryFn: () => cohortService.getCohorts(),
  });

  // Derived Cohort Data
  const studentCohorts = useMemo(() => {
    if (!studentId || !allCohorts.length) return [];
    return allCohorts.filter((cohort) =>
      (cohort.students || []).some((s) => s.studentId === studentId || s.id === studentId)
    );
  }, [studentId, allCohorts]);

  // Derived Financial Calculations
  const checkingBalance = financialSummary?.currentBalance ?? student?.balance ?? 0;
  const totalSavingsBalance = useMemo(() => {
    return savingsAccounts
      .filter((acc) => acc.is_active)
      .reduce((sum, acc) => sum + (Number(acc.current_amount) || 0), 0);
  }, [savingsAccounts]);

  const outstandingDebt = useMemo(() => {
    if (loanSummary) {
      return Math.max(0, (loanSummary.total_borrowed || 0) - (loanSummary.total_paid || 0));
    }
    const loans = loansData?.data || [];
    return loans
      .filter((l) => l.status === 'Approved' || l.status === 'In Progress' || l.status === 'Overdue')
      .reduce((sum, l) => sum + (Number(l.total_amount || l.amount) - Number(l.paid_amount || 0)), 0);
  }, [loanSummary, loansData]);

  const totalNetWorth = checkingBalance + totalSavingsBalance - outstandingDebt;

  // S.T.R.E.E.T. Cred calculations
  const positivePoints = behaviorCount?.positive || 0;
  const negativePoints = behaviorCount?.negative || 0;
  const netStreetCredPoints = positivePoints - negativePoints;

  const streetCentsData = useMemo(() => {
    return [positivePoints, negativePoints];
  }, [positivePoints, negativePoints]);

  // Monthly balance chart data
  const chartData = useMemo(() => {
    if (!monthlyBalance || monthlyBalance.length === 0) return [];
    return monthlyBalance.map((item) => ({
      name: moment(item.month).format('MMM'),
      value: item.balance,
    }));
  }, [monthlyBalance]);

  // Pending transactions
  const pendingTransactions = useMemo(() => {
    const raw = transactionsData?.data || [];
    return raw.filter(
      (tx) =>
        ['Pending', 'PENDING_STUDENT_ACTION'].includes(tx.status) ||
        ['Pending', 'PENDING_STUDENT_ACTION'].includes(tx.transaction_status)
    );
  }, [transactionsData]);

  // Approved transactions
  const approvedTransactions = useMemo(() => {
    const raw = transactionsData?.data || [];
    return raw
      .filter(
        (tx) =>
          !['Pending', 'PENDING_STUDENT_ACTION'].includes(tx.status) &&
          !['Pending', 'PENDING_STUDENT_ACTION'].includes(tx.transaction_status)
      )
      .filter((tx) => {
        if (txTypeFilter === 'CREDIT') return tx.transaction_type?.type?.toLowerCase() === 'credit';
        if (txTypeFilter === 'DEBIT') return tx.transaction_type?.type?.toLowerCase() === 'debit';
        return true;
      })
      .filter((tx) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
          tx.reason?.toLowerCase().includes(q) ||
          tx.transaction_type?.name?.toLowerCase().includes(q) ||
          String(tx.amount).includes(q) ||
          moment(tx.createdat).format('YYYY-MM-DD').includes(q)
        );
      });
  }, [transactionsData, txTypeFilter, searchQuery]);

  // Default Badge selection
  useEffect(() => {
    if (isBadgeModalOpen && student?.badgeid && badgeData?.data) {
      const currentBadge = badgeData.data.find((b) => b.id === student.badgeid);
      if (currentBadge) {
        setSelectedBadge({
          value: currentBadge.id,
          label: (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <img
                style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }}
                src={currentBadge.imageUrl || ''}
                alt={currentBadge.name || 'Badge'}
              />
              <Typography variant="body2" fontWeight={600}>
                {currentBadge.name}
              </Typography>
            </Stack>
          ),
        });
      }
    }
  }, [isBadgeModalOpen, student?.badgeid, badgeData?.data]);

  const handleApproveTransaction = async (txId: string) => {
    try {
      setApprovingTxId(txId);
      await transactionService.approveTransaction(txId);
      await invalidateStudentFinancialQueries(queryClient);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions', studentId] }),
        queryClient.invalidateQueries({ queryKey: ['student-financial-summary', studentId] }),
        queryClient.invalidateQueries({ queryKey: ['student', studentId] }),
      ]);
      ToastNotifications.success('Transaction approved successfully');
    } catch (error: any) {
      ToastNotifications.error(error.message || 'Failed to approve transaction');
    } finally {
      setApprovingTxId(null);
    }
  };

  const handleCancelTransaction = async (txId: string) => {
    try {
      setCancellingTxId(txId);
      await transactionService.cancelTransaction(txId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions', studentId] }),
        queryClient.invalidateQueries({ queryKey: ['student-financial-summary', studentId] }),
      ]);
      ToastNotifications.success('Transaction request denied');
    } catch (error: any) {
      ToastNotifications.error(error.message || 'Failed to cancel transaction');
    } finally {
      setCancellingTxId(null);
    }
  };

  const handleAssignBadge = async () => {
    try {
      setBadgeLoading(true);
      if (!selectedBadge?.value) {
        ToastNotifications.error('Please select a badge');
        return;
      }
      await badgeService.assignBadge(selectedBadge.value as string, studentId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['student', studentId] }),
        queryClient.invalidateQueries({ queryKey: ['students'] }),
      ]);
      ToastNotifications.success('Badge updated successfully');
      setIsBadgeModalOpen(false);
    } catch (error: any) {
      ToastNotifications.error(error.message || 'Failed to assign badge');
    } finally {
      setBadgeLoading(false);
    }
  };

  const studentFullName = student?.user
    ? `${student.user.firstname || ''} ${student.user.lastname || ''}`.trim()
    : 'Student Profile';

  const externalStudentId =
    student?.user?.extendedproperties?.studentId || student?.external_student_id || studentId.slice(0, 8);
  const studentPhone = student?.user?.extendedproperties?.phone || 'No phone on file';

  useHeaderBreadcrumbs(
    student
      ? {
          backUrl: `${routePrefix}/students`,
          backLabel: 'Students',
          breadcrumbs: [
            { label: 'Students Directory', path: `${routePrefix}/students` },
            ...(studentCohorts.length > 0
              ? [
                  {
                    label: studentCohorts[0].name,
                    path: isAdmin ? `/admin/cohorts/${studentCohorts[0].id}` : `/teacher/cohorts`,
                    isPill: true,
                  },
                ]
              : []),
            { label: studentFullName },
          ],
        }
      : null
  );

  if (isLoadingStudent) {
    return (
      <Page title="Loading Student Details...">
        <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: palette.primary.main }} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}>
            Loading student profile...
          </Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`${studentFullName || 'Student'} | Profile`}>
      <Container maxWidth="xl" sx={{ pb: 6, pt: 1, px: { xs: 1.5, sm: 2.5, lg: 3 } }}>
        {/* 1. Header Profile Banner */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: 3,
            border: '1px solid',
            borderColor: '#E2E8F0',
            bgcolor: '#FFFFFF',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
            spacing={2.5}
          >
            <Stack direction="row" spacing={2.25} alignItems="center">
              {student?.user?.photourl ? (
                <Box
                  component="img"
                  src={student.user.photourl}
                  alt={studentFullName}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `2px solid ${palette.primary.main}`,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: alpha(palette.primary.main, 0.08),
                    color: palette.primary.main,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 26,
                    fontWeight: 800,
                    border: `1.5px solid ${alpha(palette.primary.main, 0.2)}`,
                  }}
                >
                  {student?.user?.firstname?.charAt(0) || 'S'}
                </Box>
              )}

              <Box>
                {/* Title & Status */}
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.75 }}>
                  <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', color: '#0F172A' }}>
                    {studentFullName}
                  </Typography>
                  <Chip
                    label="Active Scholar"
                    size="small"
                    sx={{
                      height: 20,
                      bgcolor: alpha(palette.primary.main, 0.08),
                      color: palette.primary.main,
                      fontWeight: 700,
                      fontSize: '0.6875rem',
                      borderRadius: 1,
                    }}
                  />
                </Stack>

                {/* Structured Metadata & Badge Micro-pills */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.75 }}>
                  {/* Grade */}
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, bgcolor: '#F1F5F9', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem' }}>
                      Grade:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.75rem' }}>
                      {student?.grade || '—'}
                    </Typography>
                  </Box>

                  {/* ID */}
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, bgcolor: '#F1F5F9', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem' }}>
                      ID:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      #{externalStudentId}
                    </Typography>
                  </Box>

                  {/* Cohort */}
                  {studentCohorts.length > 0 && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, bgcolor: '#F1F5F9', borderRadius: 1 }}>
                      <Icon icon="mdi:google-classroom" width={13} color="#64748B" />
                      <Typography variant="caption" sx={{ color: '#0F172A', fontWeight: 600, fontSize: '0.75rem' }}>
                        {studentCohorts[0].name}
                      </Typography>
                    </Box>
                  )}

                  {/* Badge Award / Assign */}
                  {student?.badge ? (
                    <Tooltip title="Click to change awarded badge">
                      <Box
                        onClick={() => setIsBadgeModalOpen(true)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1,
                          py: 0.25,
                          bgcolor: alpha(palette.secondary.main, 0.12),
                          border: `1px solid ${alpha(palette.secondary.main, 0.3)}`,
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': { bgcolor: alpha(palette.secondary.main, 0.2) },
                        }}
                      >
                        <Box
                          component="img"
                          src={student.badge.imageUrl || ''}
                          alt={student.badge.name || 'Badge'}
                          sx={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover' }}
                        />
                        <Typography variant="caption" sx={{ color: '#854D0E', fontWeight: 700, fontSize: '0.75rem' }}>
                          {student.badge.name}
                        </Typography>
                        <Icon icon="mdi:pencil" width={11} color="#A16207" />
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      onClick={() => setIsBadgeModalOpen(true)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.25,
                        border: '1px dashed #CBD5E1',
                        bgcolor: '#FFFFFF',
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: palette.primary.main, bgcolor: alpha(palette.primary.main, 0.04) },
                      }}
                    >
                      <Icon icon="mdi:medal-outline" width={13} color="#64748B" />
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem' }}>
                        + Assign Badge
                      </Typography>
                    </Box>
                  )}
                </Stack>

                {/* Contact line */}
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75, color: '#64748B' }}>
                  {student?.user?.email && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Icon icon="mdi:email-outline" width={13} color="#94A3B8" />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {student.user.email}
                      </Typography>
                    </Stack>
                  )}
                  {studentPhone && studentPhone !== 'No phone on file' && (
                    <>
                      <Typography variant="caption" color="text.disabled">·</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Icon icon="mdi:phone-outline" width={13} color="#94A3B8" />
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                          {studentPhone}
                        </Typography>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Top Right Action Group */}
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<Icon icon="mdi:chart-line" width={16} />}
                onClick={() => setAnalyticsModalOpen(true)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  borderColor: '#CBD5E1',
                  color: '#334155',
                  py: 0.75,
                }}
              >
                View Analytics
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<Icon icon="mdi:account-edit-outline" width={16} />}
                onClick={() => navigate(`${routePrefix}/edit-student/${studentId}`)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  borderColor: '#CBD5E1',
                  color: '#334155',
                  py: 0.75,
                }}
              >
                Edit Profile
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<Icon icon="mdi:cash-plus" width={16} />}
                onClick={() => setTransactionModalOpen(true)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  px: 2,
                  py: 0.75,
                  boxShadow: `0 3px 10px ${alpha(palette.primary.main, 0.2)}`,
                }}
              >
                + Scholar $D
              </Button>
            </Stack>
          </Stack>
        </Card>

        {/* 2. Key 4 Financial & Behavior Metrics Cards */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {/* Available Balance */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: '#E2E8F0',
                bgcolor: '#FFFFFF',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption"
                  sx={{ color: '#64748B', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.6875rem' }}
                >
                  Checking Wallet
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(palette.primary.main, 0.08),
                    color: palette.primary.main,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icon icon="mdi:wallet-outline" width={18} />
                </Box>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <Typography variant="h4" fontWeight={800} color={palette.primary.main} sx={{ letterSpacing: '-0.02em', fontSize: '1.6rem' }}>
                  {checkingBalance.toFixed(2)} $D
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500, mt: 0.25, display: 'block' }}>
                  Liquid available cash
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Savings Vaults */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: '#E2E8F0',
                bgcolor: '#FFFFFF',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption"
                  sx={{ color: '#64748B', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.6875rem' }}
                >
                  Savings Vaults ({savingsAccounts.length})
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(palette.secondary.main, 0.12),
                    color: '#C48F12',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icon icon="mdi:piggy-bank-outline" width={18} />
                </Box>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <Typography variant="h4" fontWeight={800} color="#297041" sx={{ letterSpacing: '-0.02em', fontSize: '1.6rem' }}>
                  {totalSavingsBalance.toFixed(2)} $D
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500, mt: 0.25, display: 'block' }}>
                  Accumulated goals reserve
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Total Net Worth */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: '#E2E8F0',
                bgcolor: '#FFFFFF',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption"
                  sx={{ color: '#64748B', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.6875rem' }}
                >
                  Total Net Worth
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha('#111827', 0.06),
                    color: '#111827',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icon icon="mdi:chart-timeline-variant-shimmer" width={18} />
                </Box>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ letterSpacing: '-0.02em', fontSize: '1.6rem' }}>
                  {totalNetWorth.toFixed(2)} $D
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500, mt: 0.25, display: 'block' }}>
                  Checking + Savings - Debt
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* S.T.R.E.E.T. Cred */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: '#E2E8F0',
                bgcolor: '#FFFFFF',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption"
                  sx={{ color: '#64748B', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.6875rem' }}
                >
                  S.T.R.E.E.T. Cred
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(palette.secondary.main, 0.12),
                    color: '#C48F12',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icon icon="mdi:star-four-points-outline" width={18} />
                </Box>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color={netStreetCredPoints >= 0 ? 'success.main' : 'error.main'}
                  sx={{ letterSpacing: '-0.02em', fontSize: '1.6rem' }}
                >
                  {netStreetCredPoints >= 0 ? '+' : ''}
                  {netStreetCredPoints} pts
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500, mt: 0.25, display: 'block' }}>
                  +{positivePoints} highlights / -{negativePoints} challenges
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* 3. Streamlined Clean Navigation Tab Bar */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            bgcolor: '#FFFFFF',
            borderColor: '#E2E8F0',
            mb: 2.5,
            p: 0.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': {
                bgcolor: palette.primary.main,
                height: 2.5,
                borderRadius: 1.25,
              },
              '& .MuiTab-root': {
                minHeight: 40,
                py: 0.75,
                px: 1.75,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                color: '#64748B',
                minWidth: 'auto',
                mr: 0.5,
                transition: 'all 0.15s ease',
                '&.Mui-selected': {
                  color: palette.primary.main,
                  fontWeight: 700,
                  bgcolor: alpha(palette.primary.main, 0.08),
                },
                '&:hover': {
                  bgcolor: alpha('#F1F5F9', 0.8),
                  color: '#1E293B',
                },
              },
            }}
          >
            <Tab
              value="transactions"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:receipt-text-outline" width={17} />
                  <span>Transactions</span>
                </Stack>
              }
            />
            <Tab
              value="pending"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:clock-alert-outline" width={17} />
                  <span>Pending Approvals</span>
                  {pendingTransactions.length > 0 && (
                    <Chip
                      label={pendingTransactions.length}
                      size="small"
                      color="error"
                      sx={{ height: 18, fontSize: '0.6875rem', fontWeight: 800, px: 0.5 }}
                    />
                  )}
                </Stack>
              }
            />
            <Tab
              value="paychecks"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:cash-check" width={17} />
                  <span>Paychecks ({paychecks.length})</span>
                </Stack>
              }
            />
            <Tab
              value="savings"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:piggy-bank-outline" width={17} />
                  <span>Savings Vaults ({savingsAccounts.length})</span>
                </Stack>
              }
            />
            <Tab
              value="loans"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:handshake-outline" width={17} />
                  <span>Loans ({loansData?.count || 0})</span>
                </Stack>
              }
            />
            <Tab
              value="street-cred"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:star-outline" width={17} />
                  <span>S.T.R.E.E.T. Cred</span>
                </Stack>
              }
            />
            <Tab
              value="simulations"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:alert-decagram-outline" width={17} />
                  <span>Whammies</span>
                </Stack>
              }
            />
            <Tab
              value="cohorts"
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:google-classroom" width={17} />
                  <span>Cohorts ({studentCohorts.length})</span>
                </Stack>
              }
            />
          </Tabs>
        </Paper>

        {/* 4. Tab Content Panels */}
        {/* ========================================================================= */}
        {/* TAB 1: TRANSACTIONS LEDGER                                                */}
        {/* ========================================================================= */}
        {activeTab === 'transactions' && (
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: '#E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={1.5}
              sx={{ p: 1.75, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: '#E2E8F0' }}
            >
              <TextField
                size="small"
                placeholder="Search transactions by description, type, or date..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flex: 1,
                  maxWidth: 380,
                  bgcolor: '#FFFFFF',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': { height: 36, fontSize: '0.8125rem' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="mdi:magnify" width={18} color="#94A3B8" />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label="All"
                  size="small"
                  onClick={() => setTxTypeFilter('ALL')}
                  color={txTypeFilter === 'ALL' ? 'primary' : 'default'}
                  variant={txTypeFilter === 'ALL' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700, cursor: 'pointer', height: 26, fontSize: '0.72rem' }}
                />
                <Chip
                  label="Credits (+)"
                  size="small"
                  onClick={() => setTxTypeFilter('CREDIT')}
                  color={txTypeFilter === 'CREDIT' ? 'success' : 'default'}
                  variant={txTypeFilter === 'CREDIT' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700, cursor: 'pointer', height: 26, fontSize: '0.72rem' }}
                />
                <Chip
                  label="Debits (-)"
                  size="small"
                  onClick={() => setTxTypeFilter('DEBIT')}
                  color={txTypeFilter === 'DEBIT' ? 'error' : 'default'}
                  variant={txTypeFilter === 'DEBIT' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700, cursor: 'pointer', height: 26, fontSize: '0.72rem' }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Icon icon="mdi:plus" width={16} />}
                  onClick={() => setTransactionModalOpen(true)}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5, ml: 0.5 }}
                >
                  + New Transaction
                </Button>
              </Stack>
            </Stack>

            <Scrollbar>
              {isLoadingTransactions ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : approvedTransactions.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Icon icon="mdi:receipt-text-outline" width={40} color="#94A3B8" />
                  <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                    No transactions found
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No approved transactions match your current filters.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 600 }}>
                    <UserListHead
                      headLabel={[
                        { id: 'reason', label: 'Reason / Description', align: 'left' },
                        { id: 'type', label: 'Type', align: 'left' },
                        { id: 'date', label: 'Date', align: 'left' },
                        { id: 'amount', label: 'Amount ($D)', align: 'right' },
                        { id: 'balanceafter', label: 'Balance After', align: 'right' },
                      ]}
                    />
                    <TableBody>
                      {approvedTransactions.map((row) => {
                        const isCredit = row.transaction_type?.type?.toLowerCase() === 'credit';
                        return (
                          <TableRow key={row.id} hover sx={{ '& td': { py: 1.25 } }}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>
                                {row.reason || 'Classroom Transaction'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {row.transaction_type?.name || 'Standard'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.transaction_type?.type?.toUpperCase() || 'TX'}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontWeight: 800,
                                  fontSize: '0.65rem',
                                  bgcolor: isCredit ? alpha(palette.primary.main, 0.1) : alpha(palette.error.main, 0.1),
                                  color: isCredit ? palette.primary.main : palette.error.main,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                                {moment(row.createdat).format('MMM DD, YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="subtitle2"
                                fontWeight={800}
                                color={isCredit ? 'success.main' : 'error.main'}
                                sx={{ fontSize: '0.8125rem' }}
                              >
                                {isCredit ? '+' : '-'}
                                {Math.abs(Number(row.amount)).toFixed(2)} $D
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ fontSize: '0.8125rem' }}>
                                {Number(row.balanceafter || 0).toFixed(2)} $D
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Scrollbar>
          </Card>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: PENDING APPROVALS                                                  */}
        {/* ========================================================================= */}
        {activeTab === 'pending' && (
          <Card elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: '#E2E8F0', bgcolor: '#FFFFFF' }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
              Pending Transaction Approvals
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8125rem' }}>
              Review student transactions and debits awaiting educator review.
            </Typography>

            {pendingTransactions.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Icon icon="mdi:check-circle-outline" width={40} color="#0CAF60" />
                <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                  All clear! No pending transactions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Any new transactions requiring approval will appear here.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {pendingTransactions.map((tx) => (
                  <Paper
                    key={tx.id}
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 1.5,
                      borderColor: '#E2E8F0',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>
                        {tx.reason || 'Pending Request'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requested on {moment(tx.createdat).format('MMM DD, YYYY · h:mm A')} ·{' '}
                        <Typography
                          component="span"
                          variant="caption"
                          fontWeight={700}
                          color={tx.transaction_type?.type === 'credit' ? 'success.main' : 'error.main'}
                        >
                          {tx.transaction_type?.type === 'credit' ? '+' : '-'}
                          {Math.abs(tx.amount)} $D
                        </Typography>
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={approvingTxId === tx.id || cancellingTxId === tx.id}
                        onClick={() => handleApproveTransaction(tx.id)}
                        startIcon={
                          approvingTxId === tx.id ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <Icon icon="mdi:check" width={14} />
                          )
                        }
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, fontSize: '0.75rem', py: 0.4, px: 1.5 }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        disabled={approvingTxId === tx.id || cancellingTxId === tx.id}
                        onClick={() => handleCancelTransaction(tx.id)}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, fontSize: '0.75rem', py: 0.4, px: 1.5 }}
                      >
                        Deny
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Card>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: PAYCHECKS & PAYROLL STUBS                                          */}
        {/* ========================================================================= */}
        {activeTab === 'paychecks' && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: '#E2E8F0',
              bgcolor: '#FFFFFF',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={1.5}
              sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: '#E2E8F0' }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                  Academic Payroll & Paycheck History
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Bi-weekly Scholar Dollar payroll deposits calculated from academic grade percentage points.
                </Typography>
              </Box>
              <Chip
                label={`${paychecks.length} Paychecks`}
                size="small"
                sx={{
                  bgcolor: alpha(palette.primary.main, 0.08),
                  color: palette.primary.main,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 24,
                  borderRadius: 1,
                }}
              />
            </Stack>

            <Scrollbar>
              {isLoadingPaychecks ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : paychecks.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Icon icon="mdi:file-document-outline" width={40} color="#94A3B8" />
                  <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                    No paychecks on record
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When the school runs grade payroll, pay stubs will automatically appear here.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 600 }}>
                    <UserListHead
                      headLabel={[
                        { id: 'period', label: 'Pay Period Cycle', align: 'left' },
                        { id: 'date', label: 'Date Issued', align: 'left' },
                        { id: 'gross', label: 'Gross Pay', align: 'left' },
                        { id: 'tax', label: 'Tax Withheld', align: 'left' },
                        { id: 'amount', label: 'Net Deposit', align: 'right' },
                        { id: 'action', label: 'Action', align: 'right' },
                      ]}
                    />
                    <TableBody>
                      {paychecks.map((tx: any) => {
                        const payroll = tx.extendedproperties?.payroll as PayrollSnapshot | undefined;
                        const grossPay = payroll?.gross ?? Number(tx.amount);
                        const taxWithheld = payroll?.tax_withheld ?? 0;
                        const taxRate = payroll?.tax_rate ? Math.round(payroll.tax_rate * 100) : 0;
                        const netPay = Number(tx.amount);

                        return (
                          <TableRow key={tx.id} hover sx={{ '& td': { py: 1.25 } }}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>
                                {payroll?.pay_period_key
                                  ? payroll.pay_period_key.replace('_', ' → ')
                                  : 'Scholar Dollar Paycheck'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Bi-Weekly Academic Run
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                                {moment(tx.createdat).format('MMM DD, YYYY')}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8125rem', color: '#0F172A' }}>
                                {formatMoney(grossPay)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Typography variant="body2" fontWeight={600} color="error.main" sx={{ fontSize: '0.8125rem' }}>
                                  −{formatMoney(taxWithheld)}
                                </Typography>
                                {taxRate > 0 && (
                                  <Chip
                                    label={`${taxRate}%`}
                                    size="small"
                                    sx={{
                                      bgcolor: '#F1F5F9',
                                      color: '#475569',
                                      fontWeight: 700,
                                      fontSize: '0.65rem',
                                      height: 18,
                                      borderRadius: 0.75,
                                    }}
                                  />
                                )}
                              </Stack>
                            </TableCell>

                            <TableCell align="right">
                              <Typography
                                variant="subtitle2"
                                fontWeight={800}
                                color="success.main"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                +{formatMoney(netPay)}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Icon icon="mdi:file-document-outline" width={14} />}
                                onClick={() => {
                                  if (payroll) {
                                    setSelectedPaycheck({
                                      snapshot: payroll,
                                      amount: Number(tx.amount),
                                      createdAt: tx.createdat,
                                    });
                                  } else {
                                    setSelectedPaycheck({
                                      snapshot: {
                                        source: 'payroll_import',
                                        payroll_import_id: tx.id,
                                        pay_period_key: 'Current Pay Period',
                                        periods: {},
                                        gross: netPay,
                                        tax_rate: 0,
                                        tax_withheld: 0,
                                        net: netPay,
                                        first_name: student?.user?.firstname || '',
                                        last_name: student?.user?.lastname || '',
                                        external_student_id: externalStudentId,
                                      },
                                      amount: netPay,
                                      createdAt: tx.createdat,
                                    });
                                  }
                                }}
                                sx={{
                                  borderRadius: 1.5,
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  py: 0.4,
                                  px: 1.25,
                                }}
                              >
                                View Stub
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Scrollbar>
          </Card>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: SAVINGS VAULTS                                                     */}
        {/* ========================================================================= */}
        {activeTab === 'savings' && (
          <Stack spacing={2}>
            {isLoadingSavings ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : savingsAccounts.length === 0 ? (
              <Card elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: '#E2E8F0' }}>
                <Icon icon="mdi:piggy-bank-outline" width={40} color="#94A3B8" />
                <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                  No savings accounts found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Student has not created any savings accounts yet.
                </Typography>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {savingsAccounts.map((account) => {
                  const current = Number(account.current_amount || 0);
                  const target = Number(account.target_amount || 0);
                  const progress = target > 0 ? Math.min(100, (current / target) * 100) : 0;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={account.id}>
                      <Card
                        elevation={0}
                        sx={{
                          p: 2.25,
                          borderRadius: 2.5,
                          border: '1px solid',
                          borderColor: '#E2E8F0',
                          bgcolor: '#FFFFFF',
                        }}
                      >
                        <Stack spacing={1.75}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Typography variant="subtitle2" fontWeight={800}>
                                {account.name}
                              </Typography>
                              {account.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                                  {account.description}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={account.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              color={account.is_active ? 'success' : 'default'}
                              sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20 }}
                            />
                          </Stack>

                          <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 0.5 }}>
                              <Typography variant="h5" fontWeight={900} color={palette.primary.main}>
                                {current.toFixed(2)} $D
                              </Typography>
                              {target > 0 && (
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.72rem' }}>
                                  Goal: {target.toFixed(2)} $D ({progress.toFixed(0)}%)
                                </Typography>
                              )}
                            </Stack>
                            {target > 0 && (
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: alpha(palette.primary.main, 0.1),
                                  '& .MuiLinearProgress-bar': { bgcolor: palette.primary.main },
                                }}
                              />
                            )}
                          </Box>

                          <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, borderTop: '1px dashed', borderColor: '#E2E8F0' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Auto-Deposit Split
                            </Typography>
                            <Typography variant="caption" fontWeight={700}>
                              {account.auto_deposit_percentage || 0}% of deposits
                            </Typography>
                          </Stack>
                        </Stack>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Stack>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: LOANS & BORROWING                                                  */}
        {/* ========================================================================= */}
        {activeTab === 'loans' && (
          <Stack spacing={2}>
            <Card elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: '#E2E8F0', bgcolor: '#F8FAFC' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.6875rem' }}>
                    TOTAL BORROWED
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="text.primary">
                    ${(loanSummary?.total_borrowed || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.6875rem' }}>
                    TOTAL REPAID
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="success.main">
                    ${(loanSummary?.total_paid || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.6875rem' }}>
                    REMAINING OBLIGATION
                  </Typography>
                  <Typography variant="h6" fontWeight={900} color={outstandingDebt > 0 ? 'error.main' : 'text.primary'}>
                    ${outstandingDebt.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: '#E2E8F0', bgcolor: '#FFFFFF' }}>
              <Scrollbar>
                {isLoadingLoans ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : !loansData?.data || loansData.data.length === 0 ? (
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <Icon icon="mdi:hand-coin-outline" width={40} color="#94A3B8" />
                    <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                      No loans on record
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Student has not submitted any loan applications.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table sx={{ minWidth: 600 }}>
                      <UserListHead
                        headLabel={[
                          { id: 'reason', label: 'Purpose / Reason', align: 'left' },
                          { id: 'amount', label: 'Principal', align: 'left' },
                          { id: 'term', label: 'Term', align: 'left' },
                          { id: 'monthly', label: 'Monthly Payment', align: 'left' },
                          { id: 'status', label: 'Status', align: 'center' },
                          { id: 'action', label: 'Action', align: 'right' },
                        ]}
                      />
                      <TableBody>
                        {loansData.data.map((loan) => (
                          <TableRow key={loan.id} hover sx={{ '& td': { py: 1.25 } }}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>
                                {loan.reason || 'Classroom Loan'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Applied on {moment(loan.createdat).format('MMM DD, YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.8125rem' }}>
                                ${Number(loan.amount).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                                {loan.term_months} months
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
                                ${Number(loan.monthly_payment || 0).toFixed(2)}/mo
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={loan.status}
                                size="small"
                                color={
                                  loan.status === 'Approved' || loan.status === 'Paid'
                                    ? 'success'
                                    : loan.status === 'Pending'
                                    ? 'warning'
                                    : 'error'
                                }
                                sx={{ fontWeight: 800, fontSize: '0.6875rem', height: 20 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Icon icon="mdi:eye-outline" width={14} />}
                                onClick={() => navigate(`${routePrefix}/view-loan/${loan.id}`)}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, fontSize: '0.75rem', py: 0.4 }}
                              >
                                View Loan
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Scrollbar>
            </Card>
          </Stack>
        )}

        {/* ========================================================================= */}
        {/* TAB 6: S.T.R.E.E.T. CRED BEHAVIOR LOG                                     */}
        {/* ========================================================================= */}
        {activeTab === 'street-cred' && (
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: '#E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={1.5}
              sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: '#E2E8F0' }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                  Behavior Event Timeline
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  +{positivePoints} Highlights · -{negativePoints} Challenges · Net {netStreetCredPoints >= 0 ? `+${netStreetCredPoints}` : netStreetCredPoints} pts ({positivePoints + negativePoints > 0 ? Math.round((positivePoints / (positivePoints + negativePoints)) * 100) : 100}% positive)
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon icon="mdi:chart-donut" width={16} />}
                  onClick={() => setAnalyticsModalOpen(true)}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    borderColor: '#CBD5E1',
                    bgcolor: '#FFFFFF',
                    color: '#334155',
                  }}
                >
                  View Ratio Analytics
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Icon icon="mdi:plus" width={16} />}
                  onClick={() => navigate(`${routePrefix}/add-student-behaviour/${studentId}`)}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                >
                  + Log Behavior
                </Button>
              </Stack>
            </Stack>

            <Scrollbar>
              {!streetCredData?.data || streetCredData.data.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Icon icon="mdi:star-four-points-outline" width={40} color="#94A3B8" />
                  <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                    No behavior entries recorded
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click &quot;+ Log Behavior&quot; to award points or log character challenges.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 600 }}>
                    <UserListHead
                      headLabel={[
                        { id: 'title', label: 'Core Value / Action', align: 'left' },
                        { id: 'date', label: 'Date Logged', align: 'left' },
                        { id: 'points', label: 'Points', align: 'left' },
                        { id: 'type', label: 'Type', align: 'center' },
                      ]}
                    />
                    <TableBody>
                      {streetCredData.data.map((b) => {
                        const isPositive = b.behaviour_types?.type?.toLowerCase() !== 'challenge';
                        return (
                          <TableRow key={b.id} hover sx={{ '& td': { py: 1.25 } }}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>
                                {b.behaviour_types?.title || 'Behavior Log'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                                {moment(b.createdAt).format('MMM DD, YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="subtitle2"
                                fontWeight={800}
                                color={isPositive ? 'success.main' : 'error.main'}
                                sx={{ fontSize: '0.8125rem' }}
                              >
                                {isPositive ? '+' : '-'}
                                {b.behaviour_types?.points || 0} pts
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={isPositive ? 'Highlight' : 'Challenge'}
                                size="small"
                                color={isPositive ? 'success' : 'error'}
                                sx={{ fontWeight: 800, fontSize: '0.6875rem', height: 20 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Scrollbar>
          </Card>
        )}

        {/* ========================================================================= */}
        {/* TAB 7: FINANCIAL SIMULATIONS / WHAMMIES                                   */}
        {/* ========================================================================= */}
        {activeTab === 'simulations' && (
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: '#E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.75, borderBottom: '1px solid', borderColor: '#E2E8F0' }}>
              <Typography variant="subtitle1" fontWeight={800}>
                Assigned Whammy Challenges
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<Icon icon="mdi:plus" width={16} />}
                onClick={() => navigate(`${routePrefix}/assign-new-chellenge/${studentId}`)}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
              >
                + Assign Whammy
              </Button>
            </Stack>

            <Scrollbar>
              {isLoadingSimulations ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : !financialSimulations?.data || financialSimulations.data.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Icon icon="mdi:alert-decagram-outline" width={40} color="#94A3B8" />
                  <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                    No Whammies assigned
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Assign budgeting challenges to let scholars practice financial decision making.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 600 }}>
                    <UserListHead
                      headLabel={[
                        { id: 'scenario', label: 'Scenario Name', align: 'left' },
                        { id: 'duration', label: 'Duration', align: 'left' },
                        { id: 'amount', label: 'Budget', align: 'left' },
                        { id: 'status', label: 'Status', align: 'center' },
                        { id: 'action', label: 'Action', align: 'right' },
                      ]}
                    />
                    <TableBody>
                      {financialSimulations.data.map((sim) => (
                        <TableRow key={sim.id} hover sx={{ '& td': { py: 1.25 } }}>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>
                              {sim.scenario?.title || 'Interactive Scenario'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                              {sim.duration} months
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.8125rem' }}>
                              ${sim.amount}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={sim.status || 'Active'}
                              size="small"
                              color={sim.status === 'Completed' ? 'success' : 'warning'}
                              sx={{ fontWeight: 800, fontSize: '0.6875rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`${routePrefix}/view-financial-simulations/${sim.id}`)}
                            >
                              <Icon icon="mdi:eye" width={18} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Scrollbar>
          </Card>
        )}

        {/* ========================================================================= */}
        {/* TAB 8: COHORTS & CLASSES                                                  */}
        {/* ========================================================================= */}
        {activeTab === 'cohorts' && (
          <Grid container spacing={2}>
            {studentCohorts.length === 0 ? (
              <Grid item xs={12}>
                <Card elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: '#E2E8F0' }}>
                  <Icon icon="mdi:google-classroom" width={40} color="#94A3B8" />
                  <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                    No cohort enrollments
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This student is not currently enrolled in any class cohorts.
                  </Typography>
                </Card>
              </Grid>
            ) : (
              studentCohorts.map((cohort) => (
                <Grid item xs={12} sm={6} md={4} key={cohort.id}>
                  <Card elevation={0} sx={{ p: 2.25, borderRadius: 2.5, border: '1px solid', borderColor: '#E2E8F0', bgcolor: '#FFFFFF' }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight={800}>
                          {cohort.name}
                        </Typography>
                        <Chip label="Enrolled" size="small" color="primary" sx={{ fontWeight: 700, fontSize: '0.6875rem', height: 20 }} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                        Class: {cohort.classes?.name || cohort.className || 'General Class'}
                      </Typography>
                      {cohort.teachers && cohort.teachers.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Educator:{' '}
                          <Typography component="span" variant="caption" fontWeight={700} color="text.primary">
                            {cohort.teachers.map((t) => `${t.firstname} ${t.lastname}`).join(', ')}
                          </Typography>
                        </Typography>
                      )}
                      {isAdmin && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/admin/cohorts/${cohort.id}`)}
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, fontSize: '0.75rem', py: 0.4, mt: 0.5 }}
                        >
                          View Cohort Hub
                        </Button>
                      )}
                    </Stack>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {/* ========================================================================= */}
        {/* MODALS: Analytics, Transaction, Badge Assignment, Paycheck Stub View      */}
        {/* ========================================================================= */}
        {/* 1. Analytics & Insights Modal (Financial & Behavioral) */}
        <Dialog
          open={isAnalyticsModalOpen}
          onClose={() => setAnalyticsModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: '#FFFFFF',
              boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.18)',
              overflow: 'hidden',
            },
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              px: 3,
              py: 2.25,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #E2E8F0',
              bgcolor: '#F8FAFC',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: alpha(palette.primary.main, 0.1),
                  color: palette.primary.main,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Icon icon="mdi:chart-box-outline" width={22} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} color="#0F172A">
                  Financial & Behavioral Analytics
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Performance trajectory, monthly balance trends, and S.T.R.E.E.T. Cred character breakdown for {studentFullName}
                </Typography>
              </Box>
            </Stack>

            <IconButton size="small" onClick={() => setAnalyticsModalOpen(false)} sx={{ color: '#64748B' }}>
              <Icon icon="mdi:close" width={20} />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 3, bgcolor: '#FFFFFF' }}>
            {/* 4 Summary KPI Ribbon */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, borderColor: '#E2E8F0', bgcolor: '#F8FAFC' }}>
                  <Typography variant="caption" color="#64748B" fontWeight={700} sx={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Available Checking
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color={palette.primary.main} sx={{ mt: 0.5 }}>
                    {checkingBalance.toFixed(2)} $D
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                    Liquid wallet cash
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, borderColor: '#E2E8F0', bgcolor: '#F8FAFC' }}>
                  <Typography variant="caption" color="#64748B" fontWeight={700} sx={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Total Credits Earned
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="success.main" sx={{ mt: 0.5 }}>
                    +{financialSummary?.totalCredit || 0} $D
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                    Cumulative rewards & pay
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, borderColor: '#E2E8F0', bgcolor: '#F8FAFC' }}>
                  <Typography variant="caption" color="#64748B" fontWeight={700} sx={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Total Debits Spent
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="error.main" sx={{ mt: 0.5 }}>
                    -{financialSummary?.totalDebit || 0} $D
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                    Store, fines & fees
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, borderColor: '#E2E8F0', bgcolor: '#F8FAFC' }}>
                  <Typography variant="caption" color="#64748B" fontWeight={700} sx={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Net Character Cred
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color={netStreetCredPoints >= 0 ? 'success.main' : 'error.main'}
                    sx={{ mt: 0.5 }}
                  >
                    {netStreetCredPoints >= 0 ? '+' : ''}{netStreetCredPoints} pts
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                    {positivePoints + negativePoints > 0 ? Math.round((positivePoints / (positivePoints + negativePoints)) * 100) : 100}% positive ratio
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Dual Chart Breakdown Grid */}
            <Grid container spacing={2.5}>
              {/* Left Column: Monthly Balance Trajectory */}
              <Grid item xs={12} md={7}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2.25,
                    borderRadius: 2.5,
                    border: '1px solid #E2E8F0',
                    bgcolor: '#FFFFFF',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon icon="mdi:chart-timeline-variant" width={18} color={palette.primary.main} />
                      <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                        Monthly Balance Trajectory ($D)
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      Historical monthly balance growth and cash accumulation trend
                    </Typography>
                  </Box>

                  <Box sx={{ height: 260, width: '100%' }}>
                    <LineChart label="Average Monthly Balance" data={chartData} />
                  </Box>
                </Card>
              </Grid>

              {/* Right Column: S.T.R.E.E.T. Cred Behavior Donut & Ratio */}
              <Grid item xs={12} md={5}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2.25,
                    borderRadius: 2.5,
                    border: '1px solid #E2E8F0',
                    bgcolor: '#FFFFFF',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon icon="mdi:chart-donut" width={18} color="#C48F12" />
                      <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                        Behavior Ratio (S.T.R.E.E.T. Cred)
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      Balance between character highlights and logged challenges
                    </Typography>
                  </Box>

                  <Box sx={{ py: 1 }}>
                    <DonutChart
                      label="S.T.R.E.E.T Cred"
                      data={streetCentsData}
                      labels={['Highlights', 'Challenges']}
                      colors={[palette.primary.main, palette.error.main]}
                    />
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                      pt: 1.5,
                      borderTop: '1px solid #E2E8F0',
                      bgcolor: '#F8FAFC',
                      p: 1.25,
                      borderRadius: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="#64748B" fontWeight={700} sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>
                        Highlights
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={800} color="success.main">
                        +{positivePoints}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="#64748B" fontWeight={700} sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>
                        Challenges
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={800} color="error.main">
                        -{negativePoints}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="#64748B" fontWeight={700} sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>
                        Positive Rate
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                        {positivePoints + negativePoints > 0 ? Math.round((positivePoints / (positivePoints + negativePoints)) * 100) : 100}%
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
            <Button
              variant="contained"
              onClick={() => setAnalyticsModalOpen(false)}
              sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', px: 3 }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* 2. Transaction Modal */}
        <TransactionModal
          open={isTransactionModalOpen}
          closeModal={() => setTransactionModalOpen(false)}
          studentId={studentId}
        />

        {/* 3. Paycheck Stub Modal */}
        <Dialog
          open={!!selectedPaycheck}
          onClose={() => setSelectedPaycheck(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          {selectedPaycheck && (
            <PaycheckStub
              snapshot={selectedPaycheck.snapshot}
              studentName={studentFullName}
              transactionAmount={selectedPaycheck.amount}
              createdAt={selectedPaycheck.createdAt}
              onClose={() => setSelectedPaycheck(null)}
            />
          )}
        </Dialog>

        {/* 4. Assign Badge Modal */}
        <BootstrapDialog
          open={isBadgeModalOpen}
          onClose={() => setIsBadgeModalOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <Box sx={{ px: 2.5, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={800}>
              Assign Student Badge
            </Typography>
            <IconButton size="small" onClick={() => setIsBadgeModalOpen(false)}>
              <Icon icon="mdi:close" width={20} />
            </IconButton>
          </Box>
          <DialogContent dividers sx={{ my: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8125rem' }}>
              Select a badge to recognize academic growth or milestone achievement:
            </Typography>
            <Select<BadgeOption>
              options={badgeData?.data.map((b) => ({
                label: (
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <img
                      style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }}
                      src={b.imageUrl || ''}
                      alt={b.name || 'Badge'}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {b.name}
                    </Typography>
                  </Stack>
                ),
                value: b.id,
              }))}
              onChange={(val) => setSelectedBadge(val)}
              placeholder="Choose a badge..."
              value={selectedBadge}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2 }}>
            <Button variant="outlined" color="inherit" onClick={() => setIsBadgeModalOpen(false)} sx={{ borderRadius: 2, fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={badgeLoading}
              onClick={handleAssignBadge}
              sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
            >
              {badgeLoading ? <CircularProgress size={18} color="inherit" /> : 'Save Badge'}
            </Button>
          </DialogActions>
        </BootstrapDialog>

      </Container>
    </Page>
  );
}
